import { fetchStockSymbols } from '../utils/stockMetadataBlob';

// Cache for stock list
let cachedStockList: string[] = [];
let isLoading = false;
let loadPromise: Promise<string[]> | null = null;

/**
 * Load stock list directly from trading-data-v2/raw/stockmetadata.parquet
 * (no backend round-trip — uses the same SAS token as the watchlist).
 */
export async function loadStockList(): Promise<string[]> {
  if (cachedStockList.length > 0) return cachedStockList;
  if (isLoading && loadPromise) return loadPromise;

  isLoading = true;
  loadPromise = (async () => {
    try {
      const symbols = await fetchStockSymbols();
      if (symbols.length > 0) {
        cachedStockList = symbols.includes('IDX') ? symbols : [...symbols, 'IDX'].sort();
        console.log(`✅ Loaded ${cachedStockList.length} stocks from blob parquet`);
        return cachedStockList;
      }
      console.error('❌ fetchStockSymbols returned empty list');
      cachedStockList = ['IDX'];
      return cachedStockList;
    } catch (error) {
      console.error('❌ Error loading stock list from blob:', error);
      cachedStockList = ['IDX'];
      return cachedStockList;
    } finally {
      isLoading = false;
      loadPromise = null;
    }
  })();

  return loadPromise;
}

/**
 * Get the cached stock list (synchronous).
 * Call loadStockList() first to populate it.
 */
export function getStockList(): string[] {
  return cachedStockList;
}

/**
 * Proxy so existing code that reads STOCK_LIST synchronously still works
 * once loadStockList() has been awaited.
 */
export const STOCK_LIST = new Proxy([] as string[], {
  get(_target, prop) {
    if (prop === 'length' || typeof prop === 'symbol' || !isNaN(Number(prop))) {
      return (cachedStockList as any)[prop];
    }
    const value = (cachedStockList as any)[prop];
    if (typeof value === 'function') return value.bind(cachedStockList);
    return value;
  }
});

export const getStocksByPrefix = (prefix: string): string[] => {
  const list = getStockList();
  if (!prefix) return list;
  return list.filter(s => s.toLowerCase().startsWith(prefix.toLowerCase()));
};

export const searchStocks = (query: string): string[] => {
  const list = getStockList();
  if (!query) return list;
  return list.filter(s => s.toLowerCase().includes(query.toLowerCase()));
};
